import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InputAdornment from "@mui/material/InputAdornment";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { PenIcon } from "../components/svgicons/SvgIcons";
import FancyTooltip from "../components/utils/FancyTooltip";
import FancyTablePagination from "../components/utils/FancyTablePagination";
import FancyPaper from "../components/FancyPaper";
import { properties } from "../utils/Properties_es";
import {
  AguinaldoDetail,
  AguinaldoListItem,
  AguinaldoMonthly,
  getAguinaldoDetail,
  getAguinaldoList,
  saveAguinaldoEntry,
} from "../services/aguinaldos/AguinaldosService";
import { useApp } from "../hooks/useApp";

dayjs.locale("es");

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const formatPeriodDate = (date: string) => {
  const formatted = dayjs(date).format("D MMM YYYY");
  return formatted.replace(/\s([a-zá-úñ])/i, (m) => m.toUpperCase());
};

// Intl.NumberFormat("es-CR", ...) agrupa los miles con un espacio, no con
// punto — no hay locale de Intl que dé "punto de miles, coma decimal" y
// símbolo ₡ de forma confiable a la vez. Se arma el número con un locale que
// sí agrupa con punto (de-DE) y se antepone el símbolo a mano.
const formatCRC = (value: number) =>
  `₡${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

const buildYearOptions = () => {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3];
};

const ROWS_PER_PAGE = 10;

const modalCellHeadSx = {
  color: "#222",
  fontWeight: 700,
  fontSize: 14,
  borderBottom: "1px solid #E5E5E5",
  py: 1.5,
  background: "#FFF",
};

const ReadOnlyField = ({
  label,
  value,
  flex,
  minWidth,
  endIcon,
}: {
  label: string;
  value: string;
  flex: number;
  minWidth: number;
  endIcon?: React.ReactNode;
}) => (
  <Box sx={{ flex, minWidth, display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#222" }}>{label}</Typography>
    <TextField
      value={value}
      InputProps={{
        readOnly: true,
        notched: false,
        endAdornment: endIcon ? (
          <InputAdornment position="end" sx={{ color: "#9E9E9E" }}>
            {endIcon}
          </InputAdornment>
        ) : undefined,
      }}
      inputProps={{ style: { padding: "10px 14px" } }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px !important",
          background: "#FFF",
          overflow: "hidden",
          "& .MuiOutlinedInput-notchedOutline": {
            top: 0,
            borderColor: "#D9D9D9",
            borderRadius: "10px !important",
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            display: "none",
            width: 0,
            maxWidth: 0,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#BDBDBD",
          },
        },
      }}
    />
  </Box>
);

// Inserta un punto cada 3 dígitos desde la derecha (separador de miles).
const formatThousands = (digits: string) =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

// Deja solo dígitos + como máximo una coma + hasta 2 decimales, y reinserta
// los puntos de miles en la posición correcta. Es la máscara que ve el
// usuario mientras escribe.
const maskAmountInput = (raw: string) => {
  const cleaned = raw.replace(/[^\d,]/g, "");
  const commaIndex = cleaned.indexOf(",");
  if (commaIndex === -1) {
    return formatThousands(cleaned);
  }
  const intPart = cleaned.slice(0, commaIndex).replace(/,/g, "");
  const decPart = cleaned.slice(commaIndex + 1).replace(/,/g, "").slice(0, 2);
  return `${formatThousands(intPart)},${decPart}`;
};

// El valor plano ("1300800.83") que usa el resto del archivo (parseFloat,
// sumas, payload al backend), a partir de lo que se ve en el campo
// ("1.300.800,83").
const maskedToPlain = (masked: string) => {
  const [intPart, decPart] = masked.split(",");
  const intPlain = intPart.replace(/\./g, "") || "0";
  return decPart !== undefined ? `${intPlain}.${decPart}` : intPlain;
};

// La máscara a partir del valor plano guardado en el estado del formulario
// — para pintar el campo cuando cambia por fuera (abrir el modal, limpiar un
// mes, etc).
const plainToMasked = (plain: string) => {
  if (!plain) return "";
  const [intPart, decPart] = plain.split(".");
  const intFormatted = formatThousands(intPart || "0");
  return decPart !== undefined ? `${intFormatted},${decPart}` : intFormatted;
};

// El backend manda los montos como número y JSON pierde los ceros finales
// (1200000.00 llega como 1200000, 1200000.50 como 1200000.5): se fijan los 2
// decimales al cargar, igual que hace el campo al perder el foco.
const toAmountString = (value: number) => (Number(value) || 0).toFixed(2);

// Campo de monto con máscara en vivo: el punto de miles se inserta solo a
// medida que se escriben dígitos, y una sola coma activa los decimales (máx.
// 2). El "₡" es un prefijo fijo (InputAdornment), no texto editable — así
// nunca se manda al backend un valor mal interpretado como pasaba antes con
// parseFloat("1.000,83") === 1. En modo lectura se usa formatCRC (siempre 2
// decimales) para que coincida con las demás celdas del listado.
const ModalPillInput = ({
  value,
  onChange,
  minWidth = "11ch",
  readOnly = false,
}: {
  value: string;
  onChange: (v: string) => void;
  minWidth?: number | string;
  readOnly?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingCursor, setPendingCursor] = useState<number | null>(null);
  const masked = readOnly ? formatCRC(parseFloat(value) || 0) : plainToMasked(value);

  useLayoutEffect(() => {
    if (pendingCursor !== null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCursor, pendingCursor);
      setPendingCursor(null);
    }
  }, [pendingCursor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cursorPos = e.target.selectionStart ?? rawValue.length;

    // Solo importa cuántos caracteres "de contenido" (dígitos o coma) hay
    // antes del cursor — los puntos de miles son puramente decorativos, así
    // que no cuentan para recolocarlo después de re-formatear.
    const contentBeforeCursor = rawValue
      .slice(0, cursorPos)
      .replace(/[^\d,]/g, "").length;

    const newMasked = maskAmountInput(rawValue);

    let newCursor = newMasked.length;
    if (contentBeforeCursor === 0) {
      newCursor = 0;
    } else {
      let seen = 0;
      for (let i = 0; i < newMasked.length; i++) {
        if (/[\d,]/.test(newMasked[i])) {
          seen++;
          if (seen === contentBeforeCursor) {
            newCursor = i + 1;
            break;
          }
        }
      }
    }

    setPendingCursor(newCursor);
    onChange(maskedToPlain(newMasked));
  };

  const handleBlur = () => {
    if (readOnly) return;
    // "Siempre dos decimales": al salir del campo se completa el formato,
    // sin forzarlo mientras se está escribiendo.
    onChange((parseFloat(value) || 0).toFixed(2));
  };

  // Selecciona todo el contenido al entrar al campo: así el primer dígito
  // que se escriba reemplaza el "0" inicial en vez de pegarse antes o
  // después de él (el problema de tener que borrarlo a mano).
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!readOnly) e.target.select();
  };

  return (
    <TextField
      inputRef={inputRef}
      type="text"
      inputMode="decimal"
      value={masked}
      onChange={readOnly ? undefined : handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      InputProps={{
        notched: false,
        readOnly,
        startAdornment: readOnly ? undefined : (
          <InputAdornment position="start" sx={{ mr: 0.25, color: "#6B6B6B" }}>
            ₡
          </InputAdornment>
        ),
      }}
      inputProps={{
        style: { textAlign: "center", padding: "6px 12px 6px 0" },
      }}
      sx={{
        width: "100%",
        minWidth,
        maxWidth: "100%",
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px !important",
          background: "#FFF",
          overflow: "hidden",
          pl: readOnly ? undefined : 1.5,
          "& .MuiOutlinedInput-notchedOutline": {
            top: 0,
            borderColor: "#D9D9D9",
            borderRadius: "12px !important",
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            display: "none",
            width: 0,
            maxWidth: 0,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#BDBDBD",
          },
        },
      }}
    />
  );
};

/**
 * Qué se manda como salario ordinario digitado a mano.
 *
 * Cero (o campo vacío) significa "que lo calcule el sistema", y un mes que quedó
 * igual al monto calculado se manda como null: así el que no se tocó sigue
 * recalculándose solo cuando más adelante entren sus planillas, en vez de
 * quedar congelado con el valor que la pantalla mostraba al abrirse.
 */
const resolveSalarioManual = (
  month: AguinaldoMonthly,
  value: string
): number | null => {
  const typed = parseFloat(value) || 0;
  if (typed === 0) return null;
  const calculated = month.salarioOrdinarioCalculado ?? 0;
  return Math.round(typed * 100) === Math.round(calculated * 100) ? null : typed;
};

/* ─── component ────────────────────────────────────────────────────────────── */
const ManagementAguinaldos = () => {
  const theme = useTheme();
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const { authInfo, setErrorMsg: setAppError, setSuccessMsg } = useApp();
  const printRef = useRef<HTMLDivElement>(null);

  /* list state */
  const [selectedYear, setSelectedYear] = useState(yearOptions[0]);
  const [preFilter, setPreFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<AguinaldoListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  /* dialog state */
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AguinaldoListItem | null>(null);
  const [detail, setDetail] = useState<AguinaldoDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [monthSalario, setMonthSalario] = useState<string[]>(
    Array(12).fill("0")
  );
  const [monthMaternidad, setMonthMaternidad] = useState<string[]>(
    Array(12).fill("0")
  );
  const [pensionCantidad, setPensionCantidad] = useState("0");
  const [pensionMonto, setPensionMonto] = useState("0");
  const [saving, setSaving] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  /* ── load list ── */
  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const data = await getAguinaldoList(selectedYear, search);
      setRows(data);
      setPage(0);
    } catch (e: any) {
      setListError(e.message ?? "Error al cargar aguinaldos");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, search]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleApplyFilter = async () => {
    setSearch(preFilter);
  };

  const handleResetSearch = async () => {
    setPreFilter("");
    setSearch("");
  };

  /* ── open / close dialog ── */
  const handleOpen = async (row: AguinaldoListItem) => {
    setSelectedItem(row);
    setOpenDialog(true);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const data = await getAguinaldoDetail(row.personId, selectedYear);
      setDetail(data);
      setMonthSalario(data.months.map((m) => toAmountString(m.salarioOrdinario)));
      setMonthMaternidad(data.months.map((m) => toAmountString(m.maternidadPaternidad)));
      // Si se guardó un desglose previo (cantidad × monto), recuperarlo.
      // Si sólo hay total legacy (sin desglose), inicializar como cantidad=1.
      if (data.pensionQuantity > 0 || data.pensionUnitAmount > 0) {
        setPensionCantidad(toAmountString(data.pensionQuantity));
        setPensionMonto(toAmountString(data.pensionUnitAmount));
      } else if (data.globalDeductions > 0) {
        setPensionCantidad(toAmountString(1));
        setPensionMonto(toAmountString(data.globalDeductions));
      } else {
        setPensionCantidad(toAmountString(0));
        setPensionMonto(toAmountString(0));
      }
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al cargar detalle");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDetail(null);
    setSelectedItem(null);
    setMonthSalario(Array(12).fill("0"));
    setPensionCantidad("0");
    setPensionMonto("0");
  };

  /* ── computed values (re-calculated on every render) ── */
  const totalOrdinary = monthSalario.reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const totalMaternidad = monthMaternidad.reduce(
    (s, v) => s + (parseFloat(v) || 0),
    0
  );
  const grandTotal = totalOrdinary + totalMaternidad;
  const aguinaldoBruto = grandTotal / 12;
  const pensionTotal =
    (parseFloat(pensionCantidad) || 0) * (parseFloat(pensionMonto) || 0);
  const netToPay = aguinaldoBruto - pensionTotal;

  const computedMonths =
    detail?.months.map((m, i) => ({
      ...m,
      // El salario ordinario es editable: prevalece lo que hay en pantalla.
      salarioOrdinario: parseFloat(monthSalario[i] || "0"),
      mat: parseFloat(monthMaternidad[i] || "0"),
      total:
        (parseFloat(monthSalario[i] || "0")) +
        (parseFloat(monthMaternidad[i] || "0")),
    })) ?? [];

  /* ── save ── */
  const handleSave = async () => {
    if (!detail || !selectedItem) return;
    setSaving(true);
    try {
      const pensionCantidadNum = parseFloat(pensionCantidad) || 0;
      const pensionMontoNum = parseFloat(pensionMonto) || 0;
      for (let i = 0; i < detail.months.length; i++) {
        const m = detail.months[i];
        // store pension total + breakdown in first month of the period (December)
        await saveAguinaldoEntry({
          personId: selectedItem.personId,
          year: selectedYear,
          month: m.month,
          salarioOrdinarioManual: resolveSalarioManual(m, monthSalario[i]),
          maternidadPaternidad: parseFloat(monthMaternidad[i] || "0"),
          globalDeductions: i === 0 ? pensionTotal : 0,
          pensionQuantity: i === 0 ? pensionCantidadNum : 0,
          pensionUnitAmount: i === 0 ? pensionMontoNum : 0,
          user: authInfo?.username ?? "",
        });
      }
      setSuccessMsg && setSuccessMsg("Aguinaldo guardado correctamente");
      await loadList();
      handleClose();
    } catch (e: any) {
      setAppError && setAppError(e.message ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  /* ── export / print ── */
  const handleExport = () => {
    if (!detail) return;
    const now = new Date();
    const fecha = now.toLocaleDateString("es-CR");
    const hora = now.toLocaleTimeString("es-CR");

    const rows12 = computedMonths
      .map(
        (m) =>
          `<tr>
            <td>${m.monthLabel}</td>
            <td style="text-align:right">${formatCRC(m.salarioOrdinario)}</td>
            <td style="text-align:right">${formatCRC(m.mat)}</td>
            <td style="text-align:right"><strong>${formatCRC(m.total)}</strong></td>
          </tr>`
      )
      .join("");

    const printContent = `
      <!DOCTYPE html><html lang="es"><head>
      <meta charset="UTF-8"/>
      <title>Aguinaldo - ${detail.fullName}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #222; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #c0392b; padding-bottom: 10px; margin-bottom: 16px; }
        .company { font-size: 13px; }
        .company strong { font-size: 15px; }
        .meta { text-align: right; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
        th { background: #f0f0f0; padding: 5px 8px; text-align: left; font-size: 11px; border-bottom: 1px solid #ccc; }
        td { padding: 4px 8px; border-bottom: 1px solid #eee; }
        .section-title { color: #c0392b; font-weight: bold; margin: 12px 0 4px; border-bottom: 1px solid #c0392b; padding-bottom: 2px; }
        .total-row td { background: #f0f0f0; font-weight: bold; }
        .base-calc { background: #e8f0fe; padding: 8px 12px; border-radius: 4px; display:flex; justify-content:space-between; margin-bottom:14px; }
        .deduct-total td { background: #fdecea; }
        .neto-box { background: #e8f5e9; padding: 10px 14px; border-radius: 4px; display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .neto-box .label { font-weight: bold; }
        .neto-box .amount { font-size: 18px; font-weight: bold; color: #2e7d32; }
        .firma-box { border: 1px solid #ccc; padding: 14px; border-radius: 4px; }
        @page { margin: 20mm; }
      </style>
      </head><body>
      <div class="header">
        <div class="company">
          <strong>M.R. PINTORES S.A.</strong><br/>
          Dirección: Moravia<br/>
          Teléfono: 2245-6048
        </div>
        <div class="meta">
          Fecha: ${fecha}<br/>
          Hora: ${hora}<br/>
          Página: 1
        </div>
      </div>

      <div style="margin-bottom:12px">
        <table style="width:auto;margin:0">
          <tr><td><strong>Colaborador:</strong></td><td>${detail.fullName}</td>
              <td style="padding-left:20px"><strong>Cargo:</strong></td><td>${detail.positionName}</td></tr>
          <tr><td><strong>Salario Base:</strong></td><td>${formatCRC(detail.baseSalary)}</td>
              <td style="padding-left:20px"><strong>Período:</strong></td><td>${detail.periodStart} - ${detail.periodEnd}</td></tr>
        </table>
      </div>

      <div class="section-title">Resumen de Saldos</div>
      <table>
        <thead>
          <tr><th>Mes</th><th style="text-align:right">Salario Ordinario</th>
              <th style="text-align:right">Maternidad / Paternidad</th>
              <th style="text-align:right">Total Mensual</th></tr>
        </thead>
        <tbody>
          ${rows12}
          <tr class="total-row">
            <td>TOTAL 12 MESES</td>
            <td style="text-align:right">${formatCRC(totalOrdinary)}</td>
            <td style="text-align:right">${formatCRC(totalMaternidad)}</td>
            <td style="text-align:right">${formatCRC(grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div class="base-calc">
        <div><div>Cálculo Base</div><div><small>Suma de 12 meses / 12</small></div>
             <div><small>${formatCRC(grandTotal)} / 12</small></div></div>
        <div style="text-align:right"><div>Aguinaldo Bruto</div>
             <div style="font-size:16px;font-weight:bold;color:#1565c0">${formatCRC(aguinaldoBruto)}</div></div>
      </div>

      <div class="section-title">Deducciones</div>
      <table>
        <thead>
          <tr><th>Concepto</th><th style="text-align:right">Cantidad</th>
              <th style="text-align:right">Monto</th><th style="text-align:right">Total</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Deducción Pensión Alimentaria</td>
            <td style="text-align:right">${formatCRC(parseFloat(pensionCantidad) || 0)}</td>
            <td style="text-align:right">${formatCRC(parseFloat(pensionMonto) || 0)}</td>
            <td style="text-align:right">${formatCRC(pensionTotal)}</td>
          </tr>
          <tr class="deduct-total">
            <td colspan="3"><strong>Total Deducciones:</strong></td>
            <td style="text-align:right"><strong>${formatCRC(pensionTotal)}</strong></td>
          </tr>
        </tbody>
      </table>

      <div class="neto-box">
        <div class="label">Neto a pagar</div>
        <div class="amount">${formatCRC(netToPay)}</div>
      </div>

      <div class="firma-box">
        <p>Observaciones:</p>
        <br/><br/>
        <p>Firma Recibida Conforme: __________________________________________</p>
        <p>${detail.fullName}</p>
      </div>
      </body></html>`;

    const win = window.open("", "_blank", "width=800,height=700");
    if (win) {
      win.document.write(printContent);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 500);
    }
  };

  /* ── paginated rows ── */
  const pagedRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = () => {
    setPage(0);
  };

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <FancyPaper pagetitle="Aguinaldos">
      <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 8 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={{ xs: 2, md: 4 }}
          sx={{
            background: theme.palette.secondary.light,
            borderRadius: "10px",
            padding: "15px 25px",
            width: { xs: "100%", md: "fit-content" },
          }}
        >
          <Autocomplete
            disableClearable
            options={yearOptions}
            getOptionLabel={(option: number) => String(option)}
            value={selectedYear}
            onChange={(_e, newValue) => newValue && setSelectedYear(newValue)}
            sx={{ width: { xs: "100%", md: 250 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Año"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <Stack direction="row" alignItems="center" spacing={0} sx={{ width: { xs: "100%", md: "auto" } }}>
            <TextField
              placeholder="Filtro de búsqueda"
              sx={{
                width: { xs: "100%", md: 320 },
                marginRight: "10px",
                "& .MuiInputBase-root": {
                  borderRadius: "18px",
                  background: "#FFF",
                },
                "& input": {
                  padding: "0.75rem 16px!important",
                },
                "& fieldset": {
                  borderColor: "#FFF!important",
                },
              }}
              value={preFilter}
              onChange={(e: any) => {
                setPreFilter(e.target.value);
                if (e.target.value === "") {
                  handleResetSearch();
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleApplyFilter}
              sx={{
                borderRadius: "8px!important",
                padding: "0.75rem 2rem",
                lineHeight: "1.4375em",
                background: "#FFF",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Buscar
            </Button>
          </Stack>
        </Stack>
      </Box>

      {listError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {listError}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="aguinaldos table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Colaborador</TableCell>
                  <TableCell align="center">Cédula</TableCell>
                  <TableCell align="center">Salario Base</TableCell>
                  <TableCell align="center">Monto Acumulado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.length > 0 ? (
                  pagedRows.map((row, idx) => (
                    <TableRow
                      key={`${row.personId}-${idx}`}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell align="center">{row.fullName}</TableCell>
                      <TableCell align="center">{row.dni}</TableCell>
                      <TableCell align="center">{formatCRC(row.baseSalary)}</TableCell>
                      <TableCell align="center">{formatCRC(row.accumulatedAmount)}</TableCell>
                      <TableCell align="center">
                        <FancyTooltip title="Calcular aguinaldo" placement="top">
                          <IconButton
                            aria-label="edit"
                            component="label"
                            color="primary"
                            onClick={() => handleOpen(row)}
                          >
                            <PenIcon />
                          </IconButton>
                        </FancyTooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {properties.com_parval_label_table_empty}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FancyTablePagination
            count={pagedRows.length}
            rowsPerPage={ROWS_PER_PAGE}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalElements={rows.length}
            totalPages={Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE))}
          />
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          AGUINALDO DETAIL DIALOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={openDialog}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ pb: 0, pt: 8, px: 5 }}>
          <Typography
            variant="h6"
            color="primary"
            sx={{ fontWeight: 700, fontSize: "1.2rem" }}
          >
            Cálculo de Aguinaldo
          </Typography>
          <Divider sx={{ mt: 2, borderBottomWidth: 2, borderColor: "#000" }} />
        </DialogTitle>

        <DialogContent sx={{ p: 5, pt: 4 }} ref={printRef}>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <>
              {/* ── person info ── */}
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  mt: 6,
                  mb: 6,
                  flexWrap: "wrap",
                }}
              >
                <ReadOnlyField label="Colaborador" value={detail.fullName} flex={2} minWidth={170} />
                <ReadOnlyField label="Cargo" value={detail.positionName} flex={1} minWidth={130} />
                <ReadOnlyField label="Salario Base" value={formatCRC(detail.baseSalary)} flex={1} minWidth={130} />
                <ReadOnlyField
                  label="Periodo"
                  value={`${formatPeriodDate(detail.periodStart)} - ${formatPeriodDate(detail.periodEnd)}`}
                  flex={1.5}
                  minWidth={190}
                  endIcon={<CalendarMonthIcon fontSize="small" />}
                />
              </Box>

              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: 700, fontSize: "1.2rem", mb: 1 }}
              >
                Resumen de Saldos
              </Typography>
              <Divider sx={{ mt: 2, mb: 4, borderBottomWidth: 2, borderColor: "#000" }} />

              {/* ── monthly table ── */}
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 3,
                  mb: 4,
                  boxShadow: "none",
                  background: "transparent",
                  border: "1px solid #E5D9D2",
                  overflow: "hidden",
                }}
              >
                <Table
                  sx={{
                    borderCollapse: "collapse !important",
                    borderSpacing: "0 !important",
                    "& .MuiTableCell-head": {
                      borderRadius: "0 !important",
                      background: "#FFF !important",
                    },
                    "& .MuiTableCell-root": {
                      border: "0 !important",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={modalCellHeadSx}>Mes</TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Salario Ordinario
                      </TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Maternidad / Paternidad
                      </TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Total Mensual
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {computedMonths.map((m, i) => (
                      <TableRow
                        key={`${m.year}-${m.month}`}
                        sx={{ "& td": { borderBottom: 0, pt: i === 0 ? 2.5 : 1 } }}
                      >
                        <TableCell sx={{ color: "#6B6B6B", fontSize: 14, py: 1, pl: 3 }}>
                          {m.monthLabel}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <ModalPillInput
                            value={monthSalario[i]}
                            onChange={(v) => {
                              const arr = [...monthSalario];
                              arr[i] = v;
                              setMonthSalario(arr);
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <ModalPillInput
                            value={monthMaternidad[i]}
                            onChange={(v) => {
                              const arr = [...monthMaternidad];
                              arr[i] = v;
                              setMonthMaternidad(arr);
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {formatCRC(m.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* totals row */}
                    <TableRow
                      sx={{
                        "& td": {
                          borderTop: "1px solid #E5E5E5",
                          pt: 2,
                          pb: 2,
                          background: "#FFF",
                        },
                      }}
                    >
                      <TableCell sx={{ color: "#222", fontWeight: 700, pl: 3 }}>
                        TOTAL 12 MESES
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#222" }}>
                        {formatCRC(totalOrdinary)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#222" }}>
                        {formatCRC(totalMaternidad)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#222" }}>
                        {formatCRC(grandTotal)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, px: 3 }}
              >
                El salario ordinario se calcula desde planillas y salarios
                administrativos. Si un mes no está cargado en el sistema, o el
                monto calculado no corresponde, se puede digitar a mano y ese
                monto manda. Dejarlo en 0 devuelve el mes al cálculo automático.
              </Typography>

              {/* ── calculation summary ── */}
              <Paper
                variant="outlined"
                sx={{
                  py: 3,
                  px: 25,
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 0,
                  borderColor: "#1976d2",
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Cálculo Base
                  </Typography>
                  <Typography variant="body2">Suma de 12 meses / 12</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCRC(grandTotal)} / 12
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: "#000" }}>
                    Neto a pagar
                  </Typography>
                  <Typography
                    sx={{ fontSize: "1.3rem", fontWeight: 700, lineHeight: 1.2, color: "#000" }}
                  >
                    {formatCRC(aguinaldoBruto)}
                  </Typography>
                </Box>
              </Paper>

              {/* ── deducciones ── */}
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 3,
                  mb: 4,
                  boxShadow: "none",
                  background: "transparent",
                  border: "1px solid #E5D9D2",
                  overflow: "hidden",
                }}
              >
                <Table
                  sx={{
                    borderCollapse: "collapse !important",
                    borderSpacing: "0 !important",
                    "& .MuiTableCell-head": {
                      borderRadius: "0 !important",
                      background: "#FFF !important",
                    },
                    "& .MuiTableCell-root": {
                      border: "0 !important",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={modalCellHeadSx}>Concepto</TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Cantidad
                      </TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Monto
                      </TableCell>
                      <TableCell align="center" sx={modalCellHeadSx}>
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow
                      sx={{
                        "& td": {
                          borderBottom: 0,
                          pt: 2.5,
                          pb: 2.5,
                          background: "#FFF",
                        },
                      }}
                    >
                      <TableCell sx={{ color: "#6B6B6B", fontSize: 14, pl: 3 }}>
                        Deducción Pensión Alimentaria
                      </TableCell>
                      <TableCell align="center">
                        <ModalPillInput
                          value={pensionCantidad}
                          onChange={setPensionCantidad}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <ModalPillInput
                          value={pensionMonto}
                          onChange={setPensionMonto}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <ModalPillInput
                          value={String(pensionTotal)}
                          onChange={() => {}}
                          readOnly
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "& td": {
                          pt: 2,
                          pb: 2,
                          background: "#FCEBE9",
                        },
                      }}
                    >
                      <TableCell colSpan={3} sx={{ pl: 3 }}>
                        <Typography variant="body2" fontWeight={700} color="#000">
                          Total Deducciones:
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        <Typography variant="body2" fontWeight={700} color="#000">
                          {formatCRC(pensionTotal)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ── neto a pagar ── */}
              <Paper
                variant="outlined"
                sx={{
                  py: 3,
                  px: 25,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: 0,
                  borderColor: "#81c784",
                  bgcolor: "#E8F5E9",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: "#000" }}>
                    Neto a Pagar
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    Neto a pagar - Deducciones globales
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "1.3rem", fontWeight: 700, lineHeight: 1.2, color: "#000" }}
                >
                  {formatCRC(netToPay)}
                </Typography>
              </Paper>
            </>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmCancelOpen(true)}>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={!detail}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !detail}
          >
            {saving ? <CircularProgress size={18} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          CONFIRM CANCEL DIALOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "primary.main" }}>
          ¿Cancelar cálculo?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Si cancelás, se perderán todos los cambios que no hayás guardado.
            ¿Deseás continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmCancelOpen(false)}>
            Volver
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmCancelOpen(false);
              handleClose();
            }}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </FancyPaper>
  );
};

export default ManagementAguinaldos;
